import { DrawingProgramsPicker } from '../DrawingPrograms';
import {
  Alert, Avatar, Box, Button, Chip, Divider, IconButton,
  Input,
  Modal, Option, Select, Sheet, Stack, Typography,
} from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { motion, AnimatePresence } from "framer-motion";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CoverageZonesEditor from "../CoverageZonesEditor";

import Cookies from "js-cookie";
import { APIGetTimezones, APIUpdateMyProfile, APIVerifyToken } from "../../api";



// ─── Позиции ──────────────────────────────────────────────────────────────────
const POSITION_MAP = {
  surveyor:      { label: "Обмерщик",   color: "#6366f1" },
  draftsman:     { label: "Чертежник",  color: "#0ea5e9" },
  manager:       { label: "Менеджер",   color: "#10b981" },
  quality_master:{ label: "Контроль качества", color: "#f59e0b" },
};



// ─── Строка профиля ───────────────────────────────────────────────────────────
function ProfileRow({ icon, label, value, editing, inputValue, onChange }) {
    if (!editing && !value) return null;
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
            <Box sx={{
                mt: 0.2, width: 32, height: 32, borderRadius: "10px",
                bgcolor: "#6366f112", display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, color: "#6366f1"
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 500, mb: 0.2 }}>
                    {label}
                </Typography>
                {editing && onChange ? (
                    <Input
                        size="sm"
                        value={inputValue}
                        onChange={e => onChange(e.target.value)}
                        sx={{ borderRadius: "10px" }}
                    />
                ) : (
                    <Typography level="body-sm" fontWeight={600}>{value}</Typography>
                )}
            </Box>
        </Stack>
    );
}



// ─── Основной компонент ───────────────────────────────────────────────────────
export default function ModalMyProfile() {
    const { account, setAccount, setLock, citys, modalProfile, setModalProfile, tokenEmployee, setModalKonsolConnect, setCoverageOnboardingNeeded } = useContext(LayoutContext);

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [form, setForm] = useState({});
    const [timezones, setTimezones] = useState([]);

    const [coverageZonesCount, setCoverageZonesCount] = useState(null);

    function handleLogout() {
        Cookies.remove("auth_token");
        Cookies.remove("auth_token_employee");
        setLock(true);
        setAccount({});
        setModalProfile(false);
    }
    
    async function handleSave() {
        if (saving) return;
        setSaveError('');
        if (isDraftsman && !form.drawing_program_ids?.length) {
            setSaveError('Выберите хотя бы одну программу черчения.'); return;
        }
        const token = Cookies.get('auth_token_employee') || tokenEmployee || Cookies.get('auth_token');
        const payload = { ...form };
        if (!isDraftsman) delete payload.drawing_program_ids;
        setSaving(true);
        try {
            const data = await APIUpdateMyProfile(payload, token);
            if (data?.status !== 'ok') throw new Error(data?.message || 'Не удалось сохранить профиль');
            setAccount(await APIVerifyToken(token));
            setEditing(false);
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data?.detail;
            setSaveError(typeof detail === 'string' ? detail : error.message || 'Не удалось сохранить профиль. Повторите попытку.');
        } finally { setSaving(false); }
    }

    const positions = Array.isArray(account?.position) ? account.position : [];
    const initials = account?.name
        ? account.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
        : "?";

    const isSurveyor = positions.includes('surveyor');
    const isDraftsman = positions.includes('draftsman');
    const needCoverageOnboarding = isSurveyor && coverageZonesCount === 0;
    
    useEffect(() => {
        if (modalProfile) {
            setForm({
                drawing_program_ids: (account?.drawing_programs || []).map(p => p.id),
                name: account?.name || "",
                last_name: account?.last_name || "",
                patronymic: account?.patronymic || "",
                phone: account?.phone || "",
                email: account?.email,
                max_link: account?.max_link || "",
                home_address: account?.home_address || "",
                timezone: account?.timezone || "",
                city_id: account?.city_bitrix || null,
            });
            setEditing(false);

            // Загрузить таймзоны если ещё не загружены
            if (timezones.length === 0) {
                APIGetTimezones().then(data => {
                    if (Array.isArray(data)) setTimezones(data);
                });
            }
        }
    }, [modalProfile]);

    return (
        <AnimatePresence>
        {modalProfile && (
            <Modal
            open={modalProfile}
            onClose={(e) => setModalProfile(false)}
            sx={{ 
                zIndex: 100014,
                display: "flex",
                alignItems: { xs: "flex-end", sm: "center" },
                justifyContent: "center",
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: "blur(6px)",
                        backgroundColor: "rgba(0,0,0,0.35)",
                    },
                },
            }}
            >
            {/* motion.div — анимация */}
            <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 40, scale: 0.97 }}
                transition={{ type: "spring", stiffness: 340, damping: 30 }}
                style={{ width: "100%", maxWidth: 420, outline: "none" }}
            >
                <Sheet
                    sx={{
                        width: "100%",
                        borderRadius: { xs: "20px 20px 0 0", sm: "24px" },
                        overflow: "hidden",
                        boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
                        border: "1.5px solid",
                        borderColor: "divider",
                    }}
                >
                {/* ── Шапка с градиентом ── */}
                <Box
                    sx={{
                        px: 3,
                        pt: 3,
                        pb: 2.5,
                        background: "linear-gradient(135deg, #eef2ff 0%, #f0f9ff 100%)",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        position: "relative",
                    }}
                >
                    <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={(e) => setModalProfile(false)}
                        sx={{ position: "absolute", top: 12, right: 12 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>

                    <Stack direction="row" spacing={2} alignItems="center">
                    {/* Аватар */}
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "18px",
                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                        sx={{
                            fontSize: "1.5rem",
                            fontWeight: 800,
                            color: "white",
                            fontFamily: "'Sora', sans-serif",
                        }}
                        >
                        {initials}
                        </Typography>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                        sx={{
                            fontSize: "1.15rem",
                            fontWeight: 800,
                            fontFamily: "'Sora', sans-serif",
                            letterSpacing: "-0.3px",
                            lineHeight: 1.2,
                            color: "text.primary",
                        }}
                        >
                        {account?.name || "—"}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.3 }}>
                        {account?.email || ""}
                        </Typography>
                        {/* Позиции */}
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }} useFlexGap>
                        {positions.map(pos => {
                            const meta = POSITION_MAP[pos] || { label: pos, color: "#94a3b8" };
                            return (
                            <Chip
                                key={pos}
                                size="sm"
                                variant="soft"
                                sx={{
                                bgcolor: `${meta.color}15`,
                                color: meta.color,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                border: `1px solid ${meta.color}25`,
                                height: 20,
                                }}
                            >
                                {meta.label}
                            </Chip>
                            );
                        })}
                        </Stack>
                    </Box>
                    </Stack>
                </Box>

                {/* ── Поля профиля ── */}
                <Box sx={{ px: 3, py: 2 }}>
                    <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&display=swap');`}</style>

                    <ProfileRow
                        icon={<PersonRoundedIcon fontSize="small"/>}
                        label="Имя"
                        value={account?.name}
                        editing={editing}
                        inputValue={form.name}
                        onChange={v => setForm(p => ({ ...p, name: v }))}
                    />
                    <Divider />
                    <ProfileRow
                        icon={<PersonRoundedIcon fontSize="small"/>}
                        label="Фамилия"
                        value={account?.last_name}
                        editing={editing}
                        inputValue={form.last_name}
                        onChange={v => setForm(p => ({ ...p, last_name: v }))}
                    />
                    <Divider />
                    <ProfileRow
                        icon={<PersonRoundedIcon fontSize="small"/>}
                        label="Отчество"
                        value={account?.patronymic}
                        editing={editing}
                        inputValue={form.patronymic}
                        onChange={v => setForm(p => ({ ...p, patronymic: v }))}
                    />
                    <Divider />
                    <ProfileRow 
                        icon={<EmailRoundedIcon fontSize="small"/>} 
                        label="Email"
                        value={account?.email}
                        editing={editing}
                        inputValue={form.email}
                        onChange={v => setForm(p => ({ ...p, email: v }))}
                    />
                    <Divider />
                    <ProfileRow 
                        icon={<PhoneRoundedIcon fontSize="small"/>}  
                        label="Телефон" 
                        value={account?.phone}
                        editing={editing}
                        inputValue={form.phone}
                        onChange={v => setForm(p => ({ ...p, phone: v }))}
                    />
                    <Divider />
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
                        <Box sx={{
                            mt: 0.2, width: 32, height: 32, borderRadius: "10px",
                            bgcolor: "#6366f112", display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0, color: "#6366f1"
                        }}>
                            <LocationOnRoundedIcon fontSize="small"/>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 500, mb: 0.2 }}>
                                Город
                            </Typography>
                            {editing ? (
                                <Select
                                    size="sm"
                                    placeholder="Выберите город"
                                    value={form.city_id}
                                    onChange={(_, val) => setForm(p => ({ ...p, city_id: val }))}
                                    sx={{ borderRadius: "10px" }}
                                    slotProps={{ listbox: { sx: { zIndex: 100015 } } }}
                                >
                                    {citys?.map(city => (
                                        <Option key={city.id} value={city.id}>{city.name}</Option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography level="body-sm" fontWeight={600}>
                                    {account?.city_bitrix_name || "—"}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <Divider />
                    <ProfileRow
                        icon={<LinkRoundedIcon fontSize="small"/>}
                        label="Ссылка на МАХ"
                        value={account?.max_link}
                        editing={editing}
                        inputValue={form?.max_link}
                        onChange={v => setForm(p => ({ ...p, max_link: v }))}
                    />
                    <Divider />
                    <ProfileRow 
                        icon={<HomeRoundedIcon fontSize="small"/>} 
                        label="Домашний адрес"
                        value={account?.home_address}
                        editing={editing}
                        inputValue={form.home_address}
                        onChange={v => setForm(p => ({ ...p, home_address: v }))}
                    />
                    <Divider />
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
                        <Box sx={{
                            mt: 0.2, width: 32, height: 32, borderRadius: "10px",
                            bgcolor: "#6366f112", display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0, color: "#6366f1"
                        }}>
                            <AccessTimeRoundedIcon fontSize="small"/>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 500, mb: 0.2 }}>
                                Часовой пояс
                            </Typography>
                            {editing ? (
                                <Select
                                    size="sm"
                                    placeholder="Выберите часовой пояс"
                                    value={form.timezone}
                                    onChange={(_, val) => setForm(p => ({ ...p, timezone: val }))}
                                    sx={{ borderRadius: "10px" }}
                                    slotProps={{ listbox: { sx: { zIndex: 100015 } } }}
                                >
                                    {timezones.map(tz => (
                                        <Option key={tz[0]} value={tz[0]}>{tz[1]}</Option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography level="body-sm" fontWeight={600}>
                                    {account?.timezone || "—"}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    {/* ── Зоны покрытия (только для обмерщиков) ── */}
                    {saveError && <Alert color="danger">{saveError}</Alert>}
                {isDraftsman && <Box sx={{ my: 2 }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Программы черчения</Typography>
                  {editing ? <DrawingProgramsPicker value={form.drawing_program_ids || []} disabled={saving}
                    onChange={ids => setForm(prev => ({ ...prev, drawing_program_ids: ids }))} />
                    : <Stack direction="row" gap={0.75} flexWrap="wrap">{(account?.drawing_programs || []).map(p => <Chip key={p.id} size="sm">{p.name}</Chip>)}
                      {!account?.drawing_programs?.length && <Typography level="body-sm">Программы ещё не указаны</Typography>}</Stack>}
                </Box>}
                {isSurveyor && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ py: 1 }}>
                                <CoverageZonesEditor
                                    employeeId={account?.id}
                                    token={tokenEmployee}
                                    highlightRequired={true}
                                    compact={true}
                                    readOnly={true}
                                    onChange={(zones) => {
                                        setCoverageZonesCount(zones.length);
                                        if (zones.length > 0) {
                                            setCoverageOnboardingNeeded(false);
                                        }
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </Box>

                {/* ── Кнопка выхода ── */}
                <Box sx={{ px: 3, pb: 3 }}>
                    {!editing ? (
                        <Stack spacing={1}>
                        <Button
                            fullWidth variant="soft" color="primary"
                            onClick={() => setEditing(true)}
                            sx={{ borderRadius: "14px", fontWeight: 700 }}
                        >
                            Редактировать профиль
                        </Button>
                        <Button 
                            fullWidth variant="soft" color="primary"
                            onClick={() => setModalKonsolConnect(true)}
                            sx={{ borderRadius: "14px", fontWeight: 700 }}
                        >
                            Подключить Консоль.HRM
                        </Button>
                        <Button
                            fullWidth variant="soft" color="danger"
                            startDecorator={<ExitToAppRoundedIcon />}
                            onClick={handleLogout}
                            sx={{ borderRadius: "14px", fontWeight: 700 }}
                        >
                            Выйти из аккаунта
                        </Button>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined" color="neutral" fullWidth
                            onClick={() => setEditing(false)}
                            sx={{ borderRadius: "14px" }}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="solid" color="primary" fullWidth
                            loading={saving}
                            onClick={handleSave}
                            sx={{ borderRadius: "14px", fontWeight: 700 }}
                        >
                            Сохранить
                        </Button>
                        </Stack>
                    )}
                </Box>
                </Sheet>
            </motion.div>
            </Modal>
        )}
        </AnimatePresence>
    );
}